import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Indent } from "./Indent";
import { LineHeight } from "./LineHeight";
import { TextStyleExtended } from "./TextEditor";
import { TextTransform } from "./TextTransform";
import { TextType } from "./TypographyType";
import PlaceHolder from "@tiptap/extension-placeholder";
import { RichText } from "@/models";
import { useCallback, useEffect, useState } from "react";
import { CustomStyle } from "./ColorClasses";
import { DataAttributes } from "./DataAttributes";
import ImageResize from "tiptap-extension-resize-image";
import {  ModelVariableWithView } from "./ModelQuantity";
import {
    transformMarksToNodes,
} from "@/modules/text-editor/markNodeConversions";

let timerId: ReturnType<typeof setTimeout>;
function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number,
): (...args: Parameters<T>) => void {
    return function (this: any, ...args: Parameters<T>) {
        // Specify 'this' type as 'any'
        clearTimeout(timerId);
        timerId = setTimeout(() => {
            func.apply(this, args); // 'this' context preserved
        }, delay);
    };
}

export function useDebouncedEditor({
    richText,
    isEditable = true,
    updateOverride,
    editedId,
    waitMilliseconds = 250,
    placeholder = "Add some text...",
    onBlur,
}: {
    richText: RichText | App.Data.RichTextData;
    isEditable: boolean;
    updateOverride?: (json: RichText | App.Data.RichTextData) => void;
    editedId?: string | null;
    waitMilliseconds?: number;
    placeholder?: string;
    onBlur?: (_: RichText | App.Data.RichTextData) => any | void;
}) {

    

    const debouncedUpdate = useCallback(
        debounce(function (rich_text: RichText | App.Data.RichTextData) {
            if ("function" === typeof updateOverride) {
                updateOverride(rich_text);
            }
        }, waitMilliseconds),
        [waitMilliseconds, editedId],
    );

    const [isEditingModelVariable, setIsEditingModelVariable] = useState(false);

    const editor = useEditor(
        {
            editable: isEditable,
            extensions: [
                StarterKit,
                TextType,
                Color,
                LineHeight,
                Underline,
                TextStyleExtended,
                TextTransform,
                FontFamily,
                Indent,
                TextAlign.configure({
                    types: ["heading", "paragraph", "typography"],
                }),
                PlaceHolder.configure({
                    placeholder,
                }),
                CustomStyle,
                DataAttributes,
                ImageResize,
                ModelVariableWithView,
            ],
            content: richText as RichText,
            onUpdate({ editor, transaction }) {
                transformMarksToNodes(editor);
                debouncedUpdate(editor.getJSON() as RichText);
            },
            onBlur({ editor, transaction }) {
                transformMarksToNodes(editor);
                if ("function" === typeof onBlur) {
                    onBlur(editor.getJSON() as RichText);
                }
            },
        },
        [editedId, isEditable, waitMilliseconds, updateOverride],
    );

    useEffect(() => {
        if (!editor) return;
      
        const clickHandler = (event: MouseEvent) => {
          const target = event.target as HTMLElement;
      
          if (target.closest('[data-model-variable-id], [data-time-horizon-id]')) {
            // Optional: check selection is inside the clicked node
            setIsEditingModelVariable(true);
          } else {
            setIsEditingModelVariable(false);
          }
        };
      
        const el = editor.view.dom;

        el.addEventListener('click', clickHandler);
      
        return () => {
          el.removeEventListener('click', clickHandler);
        };
      }, [editor]);

    return editor;
}
